/**
 * MaturityCriteriaPanel - Shows CISA criteria for all 4 maturity levels
 *
 * This is the primary educational component that helps users understand
 * what each maturity level means according to CISA's Zero Trust framework.
 * Users can see all levels side-by-side and understand the progression.
 */

import React, { useState, useRef, useEffect } from 'react';
import { CheckCircle, Circle, ArrowRight, ChevronDown, ChevronUp, Lightbulb } from 'lucide-react';
import { MATURITY_LEVELS } from '../../../../config/zeroTrustConstants';
import MaturityLevelBadge, { MaturityLevelDot } from './MaturityLevelBadge';
import './MaturityCriteriaPanel.css';

export default function MaturityCriteriaPanel({
  criteria, // { TRADITIONAL: string, INITIAL: string, ... }
  currentLevel = null,
  targetLevel = null,
  onSelectLevel,
  selectionMode = 'none', // 'none' | 'current' | 'target'
  expandedLevel = null, // which level card is expanded (null = all collapsed)
  onToggleExpand,
  showProgressionHints = true,
  animated = true
}) {
  const [localExpanded, setLocalExpanded] = useState(expandedLevel);
  const panelRef = useRef(null);

  // Sync local state with prop
  useEffect(() => {
    if (expandedLevel !== undefined) {
      setLocalExpanded(expandedLevel);
    }
  }, [expandedLevel]);

  const handleToggle = (levelKey) => {
    const newExpanded = localExpanded === levelKey ? null : levelKey;
    setLocalExpanded(newExpanded);
    onToggleExpand?.(newExpanded);
  };

  const handleSelect = (levelKey) => {
    if (selectionMode !== 'none' && onSelectLevel) {
      onSelectLevel(levelKey);
    }
  };

  // Calculate which levels are "achieved", "current", or "future"
  const currentIdx = MATURITY_LEVELS.findIndex(m => m.key === currentLevel);
  const targetIdx = MATURITY_LEVELS.findIndex(m => m.key === targetLevel);

  const getLevelStatus = (idx) => {
    if (currentIdx >= 0) {
      if (idx < currentIdx) return 'achieved';
      if (idx === currentIdx) return 'current';
      if (targetIdx >= 0 && idx <= targetIdx) return 'target';
    }
    return 'future';
  };

  return (
    <div
      ref={panelRef}
      className={`mcp-panel ${animated ? 'mcp-panel--animated' : ''}`}
    >
      <div className="mcp-header">
        <h3 className="mcp-title">Maturity Levels</h3>
        {showProgressionHints && (
          <p className="mcp-subtitle">
            Click a level to see detailed criteria from CISA's Zero Trust framework
          </p>
        )}
      </div>

      <div className="mcp-levels">
        {MATURITY_LEVELS.map((level, idx) => {
          const status = getLevelStatus(idx);
          const isExpanded = localExpanded === level.key;
          const isSelectable = selectionMode !== 'none';
          const isSelected =
            (selectionMode === 'current' && currentLevel === level.key) ||
            (selectionMode === 'target' && targetLevel === level.key);
          const criteriaText = criteria?.[level.key];

          return (
            <div
              key={level.key}
              className={`
                mcp-level
                mcp-level--${status}
                mcp-level--${level.key.toLowerCase()}
                ${isExpanded ? 'mcp-level--expanded' : ''}
                ${isSelectable ? 'mcp-level--selectable' : ''}
                ${isSelected ? 'mcp-level--selected' : ''}
              `.trim().replace(/\s+/g, ' ')}
              style={{
                '--level-color': level.color,
                '--level-index': idx,
                animationDelay: animated ? `${idx * 0.08}s` : '0s'
              }}
            >
              {/* Level Header */}
              <button
                className="mcp-level-header"
                onClick={() => isSelectable ? handleSelect(level.key) : handleToggle(level.key)}
                aria-expanded={isExpanded}
              >
                <div className="mcp-level-indicator">
                  {status === 'achieved' || status === 'current' ? (
                    <CheckCircle size={20} className="mcp-check-icon" />
                  ) : (
                    <Circle size={20} className="mcp-circle-icon" />
                  )}
                </div>

                <div className="mcp-level-content">
                  <div className="mcp-level-title-row">
                    <MaturityLevelBadge
                      level={level.key}
                      size="md"
                      variant={status === 'current' ? 'filled' : 'subtle'}
                    />
                    {status === 'current' && (
                      <span className="mcp-current-tag">Current</span>
                    )}
                    {status === 'target' && level.key === targetLevel && (
                      <span className="mcp-target-tag">Target</span>
                    )}
                  </div>
                  <p className="mcp-level-description">{level.description}</p>
                </div>

                <div className="mcp-level-chevron">
                  {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                </div>
              </button>

              {/* Expanded Criteria */}
              <div className={`mcp-level-details ${isExpanded ? 'mcp-level-details--open' : ''}`}>
                <div className="mcp-criteria">
                  <h4 className="mcp-criteria-title">CISA Criteria</h4>
                  <p className="mcp-criteria-text">
                    {criteriaText || 'No criteria defined for this level.'}
                  </p>
                </div>

                {showProgressionHints && idx < MATURITY_LEVELS.length - 1 && (
                  <div className="mcp-progression-hint">
                    <Lightbulb size={16} />
                    <span>
                      To progress to <strong>{MATURITY_LEVELS[idx + 1].label}</strong>:
                      Review the next level's criteria above
                    </span>
                  </div>
                )}

                {isSelectable && (
                  <button
                    className="mcp-select-btn"
                    onClick={() => handleSelect(level.key)}
                  >
                    {selectionMode === 'current' ? 'Set as Current Level' : 'Set as Target Level'}
                    <ArrowRight size={16} />
                  </button>
                )}
              </div>

              {/* Connection line to next level */}
              {idx < MATURITY_LEVELS.length - 1 && (
                <div className={`mcp-connector mcp-connector--${status}`} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * MaturityCriteriaSideBySide - Shows all 4 levels in a grid for comparison
 */
export function MaturityCriteriaSideBySide({
  criteria,
  currentLevel,
  targetLevel,
  onSelectCurrent,
  onSelectTarget,
  animated = true
}) {
  return (
    <div className={`mcp-grid ${animated ? 'mcp-grid--animated' : ''}`}>
      {MATURITY_LEVELS.map((level, idx) => {
        const isCurrent = currentLevel === level.key;
        const isTarget = targetLevel === level.key;
        const criteriaText = criteria?.[level.key];

        return (
          <div
            key={level.key}
            className={`
              mcp-grid-card
              mcp-grid-card--${level.key.toLowerCase()}
              ${isCurrent ? 'mcp-grid-card--current' : ''}
              ${isTarget ? 'mcp-grid-card--target' : ''}
            `.trim().replace(/\s+/g, ' ')}
            style={{
              '--level-color': level.color,
              animationDelay: animated ? `${idx * 0.1}s` : '0s'
            }}
          >
            <div className="mcp-grid-card-header">
              <MaturityLevelBadge level={level.key} size="md" />
              <div className="mcp-grid-card-tags">
                {isCurrent && <span className="mcp-tag mcp-tag--current">Current</span>}
                {isTarget && <span className="mcp-tag mcp-tag--target">Target</span>}
              </div>
            </div>

            <p className="mcp-grid-card-criteria">{criteriaText}</p>

            <div className="mcp-grid-card-actions">
              {!isCurrent && (
                <button
                  className="mcp-grid-btn mcp-grid-btn--current"
                  onClick={() => onSelectCurrent?.(level.key)}
                >
                  Set Current
                </button>
              )}
              {!isTarget && (
                <button
                  className="mcp-grid-btn mcp-grid-btn--target"
                  onClick={() => onSelectTarget?.(level.key)}
                >
                  Set Target
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * MaturityCriteriaCompact - Single-line criteria preview
 */
export function MaturityCriteriaCompact({ level, criteria, maxLength = 100 }) {
  const levelData = MATURITY_LEVELS.find(m => m.key === level);
  const text = criteria?.[level] || '';
  const truncated = text.length > maxLength ? text.slice(0, maxLength) + '...' : text;

  return (
    <div className="mcp-compact">
      <MaturityLevelDot level={level} size="sm" />
      <span className="mcp-compact-label" style={{ color: levelData?.color }}>
        {levelData?.label}:
      </span>
      <span className="mcp-compact-text">{truncated}</span>
    </div>
  );
}
